import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

export default function OrgSelector() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect if not Master Admin
  useEffect(() => {
    if (!user?.isMasterAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Fetch organizations
  const { data: orgsResponse, isLoading, isError } = useQuery({
    queryKey: ['admin-organizations'],
    queryFn: async () => {
      const res = await api.get('/api/admin/organizations');
      return res.data.organizations || [];
    },
    enabled: user?.isMasterAdmin,
  });

  const handleSelectOrg = (orgId) => {
    // Store the organization context in localStorage
    localStorage.setItem('viewingOrgAsAdmin', orgId);
    navigate('/dashboard');
  };

  const handleBackToAdmin = () => {
    localStorage.removeItem('viewingOrgAsAdmin');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Select Organization</h1>
              <p className="text-sm text-gray-600 mt-2">Choose an organization to view its dashboard</p>
            </div>
            <button
              onClick={handleBackToAdmin}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              ← Back to Admin Panel
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isError ? (
          <div className="text-center py-12 bg-white rounded-lg border border-red-200 p-6">
            <p className="text-red-600 font-medium">Failed to load organizations</p>
          </div>
        ) : orgsResponse?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-6">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No organizations created yet</p>
            <p className="text-sm text-gray-500 mt-2">Create your first organization in the admin panel</p>
            <button
              onClick={handleBackToAdmin}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Admin Panel
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {orgsResponse.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrg(org.id)}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-blue-500 hover:shadow-lg transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {org.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {org.memberCount} member{org.memberCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors mt-1" />
                </div>

                <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                  <p className="text-gray-600">
                    <span className="font-medium text-gray-900">Slug:</span> {org.slug}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium text-gray-900">Email:</span> {org.email}
                  </p>
                  {org.owner && (
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-900">Owner:</span> {org.owner.name}
                    </p>
                  )}
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  Created {new Date(org.createdAt).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
